import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import {
    IChecklist2ReportBenchDataByChecklists,
    IChecklist2ReportBenchUnitData,
    IChecklist2ReportDateRange,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { FiDateTimeFormatOptions } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';

export interface IChecklistBenchmarkViewData {
    ObjectName: string;
    ScheduleName: string;
    ObjectId: number;
    ScheduleId: number;
    CreatedByUserName: string;
    CreatedByUserId: number;
    DueDateTime: string; //ISO 8601 date;
    Units: IChecklist2ReportBenchUnitData[];
    CheckpointsCount: number;
}

@Component({
    selector: 'fi-checklist-history-checklists-benchmark',
    templateUrl: './checklists-benchmark.component.html',
    styleUrls: ['./checklists-benchmark.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-history-checklists-benchmark' },
    standalone: false,
})
export class FiChecklistHistoryChecklistsBenchmarkComponent implements OnChanges {
    @Input({ required: true }) checklistHistoryDateRange: IChecklist2ReportDateRange;
    @Input({ required: true }) benchmarkFilter: IChecklistV2ReportParameterFilter;
    @Input({ required: true }) auditMode: boolean;
    @Output() onShowDetailedAnswersReport = new EventEmitter<IChecklistV2ReportParameterFilter>();

    benchmarkViewData: IChecklistBenchmarkViewData[] = [];

    constructor(
        private fiChecklist2ReportHistoryService: FiChecklist2ReportHistoryService,
        private cdr: ChangeDetectorRef,
        private fiChecklist2ReportHistoryHelperService: FiChecklist2ReportHelperService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.checklistHistoryDateRange || changes.benchmarkFilter) {
            this.loadBenchDataByChecklists();
        }
    }

    private loadBenchDataByChecklists() {
        this.fiChecklist2ReportHistoryService
            .getBenchDataByChecklists(this.benchmarkFilter, this.checklistHistoryDateRange, this.auditMode)
            .then((result: IChecklist2ReportBenchDataByChecklists[]) => {
                this.benchmarkViewData = this.getBenchmarkViewData(result);
                this.cdr.markForCheck();
            });
    }

    getChecklistBenchmarkCardSizeClassName(): string {
        return this.fiChecklist2ReportHistoryHelperService.getChecklistBenchmarkCardSizeClassName(
            this.benchmarkFilter?.BusinessUnitIds?.length,
        );
    }

    getBenchmarkViewData(data: IChecklist2ReportBenchDataByChecklists[]): IChecklistBenchmarkViewData[] {
        return data.map((item: IChecklist2ReportBenchDataByChecklists): IChecklistBenchmarkViewData => {
            return {
                ObjectName: item.ChecklistName,
                ScheduleName: item.ScheduleName,
                ObjectId: item.ChecklistId,
                ScheduleId: item.ScheduleId,
                CreatedByUserId: item.ChecklistCreatedByUserId,
                CreatedByUserName: item.ChecklistCreatedByUserName,
                DueDateTime: item.DueDateTime,
                CheckpointsCount: item.CheckpointsCount,
                Units: item.Units,
            };
        });
    }

    showDetailedAnswersReport(businessUnitId: number, checklistData: IChecklistBenchmarkViewData) {
        this.onShowDetailedAnswersReport.emit({
            BusinessUnitIds: [businessUnitId],
            ChecklistIds: [checklistData.ObjectId],
            ScheduleIds: [checklistData.ScheduleId],
        } as IChecklistV2ReportParameterFilter);
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly FiDateTimeFormatOptions: Intl.DateTimeFormatOptions = FiDateTimeFormatOptions;
    protected readonly ObjectTypes = ObjectTypes;
}
